import { taskLabel, normalizeTask } from '@/lib/ctms/taskCatalog';
import { useCtmsTasks } from '@/hooks/useCtmsTasks';
import { logger } from "@/lib/logger";
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import { ESignatureModal, SignatureData } from './ESignatureModal';
import { cn } from '@/lib/utils';

interface AddDOAModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string;
  taskCategories: string[];
  /** Registry trial id to pre-select (from the trial setup checklist). */
  defaultTrialId?: string | null;
}

export function AddDOAModal({ isOpen, onClose, siteId, taskCategories, defaultTrialId }: AddDOAModalProps) {
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [trialId, setTrialId] = useState<string>(defaultTrialId || 'site');
  useEffect(() => { if (isOpen && defaultTrialId) setTrialId(defaultTrialId); }, [isOpen, defaultTrialId]);
  const { data: siteTrials = [] } = useQuery({
    queryKey: ['doa-site-trials', siteId],
    enabled: isOpen && !!siteId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trial_site_assignments')
        .select('trial_id, trials:trial_id(id, title)')
        .eq('trial_site_id', siteId)
        .eq('is_site_operational', true);
      if (error) throw error;
      return (data || []).map((r: any) => ({ id: r.trial_id, title: r.trials?.title || 'Untitled trial' }));
    },
  });
  const [effectiveDate, setEffectiveDate] = useState<Date>(new Date());
  const [expirationDate, setExpirationDate] = useState<Date | undefined>();
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  
  const queryClient = useQueryClient();
  const { canManage } = useCtmsTasks(siteId, null);

  // Get current user's staff record to determine their role
  const { data: currentUserStaff } = useQuery({
    queryKey: ['current-user-staff', siteId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('trial_site_staff')
        .select('id, staff_role, is_primary, profile_id, user_id')
        .eq('trial_site_id', siteId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        logger.error('Error fetching current user staff:', error);
        return null;
      }
      return data;
    },
    enabled: isOpen
  });

  // Get all staff members at the site
  const { data: allStaffMembers } = useQuery({
    queryKey: ['trial-site-staff', siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trial_site_staff')
        .select(`
          id,
          profile_id,
          user_id,
          staff_role,
          is_primary,
          profiles!trial_site_staff_profile_id_fkey (
            first_name,
            last_name,
            email
          )
        `)
        .eq('trial_site_id', siteId)
        .is('end_date', null);

      if (error) {
        logger.error('Error fetching staff for DOA:', error);
        throw error;
      }
      return data;
    },
    enabled: isOpen
  });

  // Filter staff based on current user's role
  // Admins and PI/primary can see all staff, regular staff can only see themselves
  const staffMembers = useMemo(() => {
    if (!allStaffMembers) return [];
    
    // If current user is not staff at this site (admin), show all staff
    if (!currentUserStaff) {
      return allStaffMembers;
    }
    
    const isPIorPrimary = canManage;
    
    if (isPIorPrimary) {
      // PI can see all staff
      return allStaffMembers;
    } else {
      // Regular staff can only see themselves
      return allStaffMembers.filter(s => s.user_id === currentUserStaff.user_id);
    }
  }, [allStaffMembers, currentUserStaff, canManage]);

  const createDOAMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!canManage) throw new Error('Only the site authority can assign duties.');
      const { data: saved, error } = await supabase
        .from('delegation_of_authority')
        .insert(data).select('id').single();

      if (error) throw error;

      return saved;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doa-records', siteId] });
      queryClient.invalidateQueries({ queryKey: ['ctms-access', siteId] });
      toast({
        title: "Delegation created",
        description: "Staff delegation has been recorded with e-signature"
      });
      handleClose();
    },
    onError: (error) => {
      toast({
        title: "Failed to create delegation",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive"
      });
    }
  });

  const handleSubmit = async () => {
    if (!canManage) return;
    if (!selectedStaff || selectedTasks.length === 0) {
      toast({
        title: "Missing information",
        description: "Please select staff member and at least one task",
        variant: "destructive"
      });
      return;
    }

    if (selectedTasks.some((task) => !normalizeTask(task)) ||
        !allStaffMembers?.some((staff) => staff.id === selectedStaff) ||
        (trialId !== 'site' && !siteTrials.some((trial) => trial.id === trialId)) ||
        (expirationDate && format(expirationDate, 'yyyy-MM-dd') < format(effectiveDate, 'yyyy-MM-dd'))) {
      toast({ title: 'Check staff, trial and dates', variant: 'destructive' });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to create delegations",
        variant: "destructive"
      });
      return;
    }

    // Store data and show signature modal
    setPendingData({
      trial_site_id: siteId,
      staff_id: selectedStaff,
      delegating_authority_id: user.id,
      delegated_tasks: selectedTasks,
      trial_id: trialId === 'site' ? null : trialId,
      effective_date: format(effectiveDate, 'yyyy-MM-dd'),
      expiration_date: expirationDate ? format(expirationDate, 'yyyy-MM-dd') : null,
      status: 'active'
    });
    setShowSignatureModal(true);
  };

  const handleSign = async (signatureData: SignatureData) => {
    if (!pendingData) return;

    const fullData = {
      ...pendingData,
      signature_data: signatureData
    };

    await createDOAMutation.mutateAsync(fullData);
  };

  const handleClose = () => {
    setSelectedStaff('');
    setSelectedTasks([]);
    setTrialId('site');
    setEffectiveDate(new Date());
    setExpirationDate(undefined);
    setPendingData(null);
    setShowSignatureModal(false);
    onClose();
  };

  const handleTaskToggle = (task: string) => {
    setSelectedTasks(prev =>
      prev.includes(task)
        ? prev.filter(t => t !== task)
        : [...prev, task]
    );
  };

  const getStaffDisplayName = (staff: any) => {
    const firstName = staff.profiles?.first_name || '';
    const lastName = staff.profiles?.last_name || '';
    const email = staff.profiles?.email || '';
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim() + (email ? ` (${email})` : '');
    }
    return email || 'Staff Member';
  };

  if (!canManage) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Delegation of Authority</DialogTitle>
            <DialogDescription>
              Delegate specific tasks to a staff member with e-signature validation
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Staff Member *</Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staffMembers?.map((staff: any) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {getStaffDisplayName(staff)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Trial</Label>
              <Select value={trialId} onValueChange={setTrialId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="site">Whole site (all trials)</SelectItem>
                  {siteTrials.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Delegated Tasks * (Select all that apply)</Label>
              <div className="grid grid-cols-2 gap-3 p-4 border rounded-lg max-h-[200px] overflow-y-auto">
                {taskCategories.map((task) => (
                  <div key={task} className="flex items-center space-x-2">
                    <Checkbox
                      id={task}
                      checked={selectedTasks.includes(task)}
                      onCheckedChange={() => handleTaskToggle(task)}
                    />
                    <Label htmlFor={task} className="text-sm font-normal cursor-pointer">
                      {taskLabel(task)}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !effectiveDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {effectiveDate ? format(effectiveDate, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={effectiveDate}
                      onSelect={(date) => date && setEffectiveDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Expiration Date (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !expirationDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {expirationDate ? format(expirationDate, "PPP") : "No expiration"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={expirationDate}
                      onSelect={setExpirationDate}
                      initialFocus
                      disabled={(date) => date < effectiveDate}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Selected: {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={createDOAMutation.isPending || !canManage}>
              {createDOAMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Continue to E-Signature'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showSignatureModal && pendingData && (
        <ESignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSign={handleSign}
          documentName="Delegation of Authority"
          signingPurpose="I confirm that I delegate the selected tasks to this staff member and take responsibility for their actions under this delegation."
          siteId={siteId}
        />
      )}
    </>
  );
}
